// Session AQT, Phase 1 design system foundation: a shared, URL-bound list-search controller,
// the search half of the new list toolbar (the sort half reuses the existing useSortableColumn).
//
// The one existing search precedent (RegisterPage.tsx) only reads `?q=` once on mount via a plain
// useState initializer and never writes back to the URL as the user types -- so it only half
// honours the restructure audit's own item-11/14/17 requirement that search live in the address
// bar. This hook is the fix for that gap: a debounced two-way binding, so a search is genuinely
// shareable/bookmarkable AND survives a back-navigation (an external URL change, e.g. the browser
// back button, resyncs the input; our own debounced write does not re-trigger itself).
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export interface ListSearchApi {
  search: string;
  setSearch: (value: string) => void;
}

export function useListSearch(paramName: string = "q", debounceMs: number = 300): ListSearchApi {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlValue = searchParams.get(paramName) ?? "";
  const [search, setSearchState] = useState(urlValue);
  const lastWritten = useRef(urlValue);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // An external URL change we did not just write ourselves (back/forward navigation, or a
  // sibling effect clearing filters) -- resync so the input reflects it.
  useEffect(() => {
    if (urlValue !== lastWritten.current) {
      setSearchState(urlValue);
      lastWritten.current = urlValue;
    }
  }, [urlValue]);

  const setSearch = useCallback(
    (value: string) => {
      setSearchState(value);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        lastWritten.current = value;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            if (value) next.set(paramName, value);
            else next.delete(paramName);
            return next;
          },
          { replace: true },
        );
      }, debounceMs);
    },
    [paramName, debounceMs, setSearchParams],
  );

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return { search, setSearch };
}

// A search-value accessor per row (the fields a free-text search should match against).
export type SearchAccessors<T> = ((row: T) => string | number | null | undefined)[];

// Case-insensitive substring match across the given accessors. A blank search returns rows
// unchanged (identity, matching sortRows' "no active column" behaviour). Pure.
export function filterRows<T>(rows: T[], search: string, accessors: SearchAccessors<T>): T[] {
  const q = search.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) =>
    accessors.some((accessor) => {
      const v = accessor(row);
      return v != null && String(v).toLowerCase().includes(q);
    }),
  );
}
